(() => {
  'use strict';
  // A separate local store and client: simulated records never reach the public API.
  const dbName = 'poptex-generation-simulation-v1', sessionKey = 'poptex.simulation.signed-in';
  const urls = new Set(), fileUrls = new Map();
  const sleep = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds));
  const store = (value, id) => new Promise((resolve, reject) => {
    const open = indexedDB.open(dbName, 1);
    open.onupgradeneeded = () => open.result.createObjectStore('records', {keyPath:'id'});
    open.onerror = () => reject(open.error);
    open.onsuccess = () => {
      const db = open.result, tx = db.transaction('records', value ? 'readwrite' : 'readonly');
      const records = tx.objectStore('records');
      const request = value ? records.put(value) : id ? records.get(id) : records.getAll();
      tx.oncomplete = () => { db.close(); resolve(value || request.result); };
      tx.onabort = tx.onerror = () => { db.close(); reject(tx.error || new Error('Local test storage unavailable')); };
    };
  });
  const snapshot = () => window.PoptexContentRegistry?.getSnapshot?.() || {};
  const signedIn = () => Boolean(window.POPTEX_CUSTOMER?.id) || sessionStorage.getItem(sessionKey) === 'true';
  const blobUrl = blob => { const url = URL.createObjectURL(blob); urls.add(url); return url; };
  const label = value => typeof value === 'string' ? value : value?.en || Object.values(value || {}).find(v => typeof v === 'string') || '';
  const sources = phone => {
    try { return JSON.parse(phone?.querySelector('[data-simulation-assets]')?.textContent || '{}'); } catch { return {}; }
  };
  const hydrate = saved => {
    if (!saved) throw new Error('Local test record unavailable');
    const record = structuredClone(saved.record), files = saved.files || {};
    const bound = field => {
      const file = files[field];
      if (!(file instanceof Blob)) return '';
      const key = saved.id + ':' + field;
      if (!fileUrls.has(key)) fileUrls.set(key,blobUrl(file));
      return fileUrls.get(key);
    };
    for (const side of ['front','back']) {
      const surface = record.caseConfig.surfaces[side];
      if (surface?.source === 'UPLOAD') surface.uploadedUrl = bound(side + 'UserImages');
    }
    const logo = bound('logoImages');
    if (logo) record.labelConfig = {...record.labelConfig, hasLogo:true, logoUrl:logo, logoName:files.logoImages.name};
    const overall = bound('frontCompositeImage') || record.caseConfig.surfaces.front?.uploadedUrl || saved.images.overall;
    const ready = Math.min(4, Math.floor(Math.max(0, Date.now() - saved.startedAt) / 2000));
    record.status = ready === 4 ? 'AI_COMPLETED' : 'AI_GENERATING';
    record.currentGeneration = {status:ready === 4 ? 'SUCCEEDED' : ready ? 'RUNNING' : 'QUEUED', previewSlots:[]};
    record.displayImages = [];
    const roles = ['PRODUCT_IMAGE','USAGE_SCENE_IMAGE','PRODUCT_DETAIL_IMAGE','PACKAGING_IMAGE'];
    ['overall','scene','detail','packaging'].forEach((slot,index) => {
      const assetId = record.id + '-' + slot;
      record.currentGeneration.previewSlots.push({slot, assetId, status:index < ready ? 'SUCCEEDED' : 'PENDING', attempts:1});
      if (index < ready) record.displayImages.push({assetId, role:roles[index], url:slot === 'overall' ? overall : saved.images[slot]});
    });
    return record;
  };
  const client = phone => ({
    getAnonymousId: () => 'local-workflow-test',
    storefront: {
      refreshCustomerSession: async () => { await sleep(450); return {authenticated:signedIn()}; },
      createCaseUploadToken: async () => { await sleep(600); return 'local-simulation-only'; }
    },
    cms: {refreshV2Options: async id => {
      const data = snapshot(), series = data.productSeries?.find(s => s.id === id), group = data.productSeriesGroups?.find(g => g.id === series?.groupId);
      return {style:{...series,version:series?.version || 1,groupVersion:group?.version || 1},group:{...group,version:group?.version || 1},
        patterns:data.patterns?.filter(p => p.productSeriesId === id).map(p => ({...p,version:p.version || 1})),referencePresets:data.referencePresets};
    }},
    cases: {
      create: async (body, options) => {
        if (options?.uploadToken !== 'local-simulation-only' || !signedIn()) throw new Error('Test sign-in required');
        await sleep(600);
        const payload = JSON.parse(body.get('payload')), data = snapshot(), assets = sources(phone);
        const style = data.productSeries?.find(s => s.id === payload.productSeriesId);
        const config = payload.caseConfig, files = {};
        for (const [key,value] of body.entries()) if (value instanceof Blob) files[key] = value;
        config.productSeries = {id:payload.productSeriesId, name:label(style?.name) || phone?.querySelector('h1')?.textContent};
        const dimensions = /^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)$/i.exec(config.size);
        if (dimensions) config.size = {widthInches:Number(dimensions[1]),heightInches:Number(dimensions[2])};
        for (const [side,surface] of Object.entries(config.surfaces)) {
          const pattern = data.patterns?.find(p => p.id === surface.patternId);
          if (pattern) { surface.pattern = {id:pattern.id, name:label(pattern.name)}; surface.sourceUrl = pattern.sourceArtworkUrl; }
          if (files[side+'UserImages']) surface.uploadName = files[side+'UserImages'].name;
        }
        const edge = data.productSeriesGroups?.flatMap(g => g.edgingOptions || []).find(e => e.id === config.edging?.optionId);
        config.edging = {...config.edging, name:phone?.querySelector('[data-review-edging]')?.textContent || label(edge?.name)};
        const packaging = data.referencePresets?.find(p => p.id === config.packaging?.presetId);
        if (packaging) config.packaging = {...config.packaging, name:label(packaging.name), previewUrl:packaging.previewUrl};
        const pattern = data.patterns?.find(p => p.id === config.surfaces.front?.patternId);
        const uuid = crypto.randomUUID?.() || [...crypto.getRandomValues(new Uint8Array(16))].map(value=>value.toString(16).padStart(2,'0')).join('');
        const id = 'SIM-' + uuid, saved = {id, startedAt:Date.now(), files,
          record:{id, caseNo:'TEST-'+id.slice(4,12).toUpperCase(), flowVersion:2, createdAt:new Date().toISOString(), caseConfig:config},
          images:{overall:pattern?.effectUrl || pattern?.sourceArtworkUrl || assets.overall, scene:assets.scene,
            detail:assets.detail, packaging:packaging ? packaging.previewUrl || assets.packaging : assets.noPackaging}};
        await store(saved);
        return {case:hydrate(saved)};
      },
      get: async id => { if (!String(id).startsWith('SIM-')) throw new Error('Not a test record'); return {case:hydrate(await store(null,id))}; },
      list: async ({page=1,pageSize=30}={}) => {
        const saved = (await store()).sort((a,b) => b.startedAt-a.startedAt);
        return {data:saved.slice((page-1)*pageSize,page*pageSize).map(hydrate), pagination:{total:saved.length}};
      },
      retrySlot: async id => ({case:hydrate(await store(null,id))})
    }
  });
  window.PoptexGenerationSimulation = {
    client, signedIn, signIn: () => sessionStorage.setItem(sessionKey,'true'),
    ownsUrl: url => urls.has(url)
  };
  window.addEventListener('pagehide', event => { if (event.persisted) return; urls.forEach(url => URL.revokeObjectURL(url)); urls.clear(); fileUrls.clear(); });
})();
